import { Input } from "antd/es";
import { TextAreaRef } from "antd/es/input/TextArea";
import { CSSProperties, Component, KeyboardEvent, createRef } from "react";
import { FrameMessageSender } from "../../components/web-components/kuika-cl-model-runtimes";

declare let window: any;

const withDoubleClickEdit = <P, S>(WrappedComponent, propertyName: string) => {
  interface IDoubleClickEditElementState {
    isEditEnabled: boolean;
  }

  // Base input styles - defined once outside the class
  const BASE_INPUT_STYLES: CSSProperties = {
    lineHeight: "unset",
    overflowWrap: "normal"
  };

  class DoubleClickEditElement<P, S> extends Component<P, IDoubleClickEditElementState> {
    editInput = createRef<TextAreaRef>();

    designer: FrameMessageSender;

    // Cache for input styles
    private cachedInputStyles: CSSProperties | null = null;
    private lastStyleRef: any = null;

    constructor(props: any) {
      super(props);
      this.designer = new FrameMessageSender();

      this.state = {
        isEditEnabled: false
      };
    }

    getInputStyles = (): CSSProperties => {
      const style = (this.props as any).style;

      // Return cached styles if style reference hasn't changed
      if (this.cachedInputStyles && this.lastStyleRef === style) {
        return this.cachedInputStyles;
      }

      this.lastStyleRef = style;

      if (style) {
        this.cachedInputStyles = {
          ...BASE_INPUT_STYLES,
          ...style
        };
      } else {
        this.cachedInputStyles = BASE_INPUT_STYLES;
      }

      return this.cachedInputStyles;
    };

    handleElementDoubleClick = (e) => {
      if (e?.detail !== 2) return;

      this.setState({ isEditEnabled: true }, () => {
        this.editInput.current.focus();
        this.editInput.current.resizableTextArea.textArea.select();
      });
    };

    handleInputOnBlur = (shouldSave: boolean = true) => {
      if (shouldSave) {
        this.designer.propertyChangedMessage({
          kuikaId: (this.props as any).kuikaid,
          propertyName,
          value: this.editInput.current.resizableTextArea.textArea.value,
          shouldClosePropertyExpand: true
        });
      }
      this.setState({ isEditEnabled: false });
    };

    handleInputOnKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
      switch (e.key) {
        case "Enter": {
          e.preventDefault();
          this.editInput.current.blur();
          break;
        }
        case "Escape": {
          this.handleInputOnBlur(false);
          break;
        }
      }
    };

    render() {
      const props = this.props as any;

      if (!window.kuika?.isDesignTime) return <WrappedComponent<P & S> {...(this.props as P)} />;

      if (this.state.isEditEnabled)
        return (
          <Input.TextArea
            defaultValue={props[propertyName]}
            ref={this.editInput}
            onKeyDown={this.handleInputOnKeyDown}
            onBlur={(e) => this.handleInputOnBlur(e.target.value !== props[propertyName])}
            bordered={false}
            style={this.getInputStyles()}
            autoSize={true}
            draggable={false}
          />
        );

      return <WrappedComponent<P & S> {...(this.props as P)} onClick={this.handleElementDoubleClick} />;
    }
  }

  return DoubleClickEditElement;
};

export default withDoubleClickEdit;
